import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles from "../../css/ai-mode/chat-bubble.module.css";
import { Block, PatentCardBlock } from "../../models/platform/messageAssistantModel";

interface ChatBubbleProps {
  message: string;
  blocks?: Block[];
  sender: "user" | "assistant";
  messageId?: string;
  onViewLog?: (assistant_message_id: string) => void;
}

// ── Patent Card ───────────────────────────────────────────────────────────────
const PatentCard: React.FC<{ block: PatentCardBlock }> = ({ block }) => (
  <div style={{
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 10,
    background: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#6366f1", letterSpacing: 0.3 }}>
        {block.patent_id}
      </span>
      <span style={{ fontSize: 11, color: "#9ca3af" }}>{block.date}</span>
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
      {block.title}
    </div>
    <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}>
      {block.summary}
    </div>
    <div style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>
      {block.company}
    </div>
  </div>
);

// ── Block Renderer ────────────────────────────────────────────────────────────
const RenderBlock: React.FC<{ block: Block }> = ({ block }) => {
  switch (block.type) {
    case "patent_card":
      return <PatentCard block={block} />;
    case "paragraph":
      return <p style={{ fontSize: 14, color: "#374151", marginBottom: 8 }}>{block.text}</p>;
    case "table":
      return (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 12 }}>
          <thead>
            <tr>
              {block.columns.map((col, i) => (
                <th key={i} style={{ textAlign: "left", padding: "6px 10px", borderBottom: "2px solid #e5e7eb", color: "#6b7280", fontWeight: 600 }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                {row.map((cell, j) => (
                  <td key={j} style={{ padding: "6px 10px", color: "#374151" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    case "section":
      return (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 4 }}>{block.title}</div>
          <p style={{ fontSize: 13, color: "#6b7280" }}>{block.content}</p>
        </div>
      );
    case "citation":
      return (
        <div style={{ fontSize: 12, color: "#6366f1", borderLeft: "3px solid #e0e7ff", paddingLeft: 8, marginBottom: 6 }}>
          <strong>{block.patent_id}</strong> — {block.text}
        </div>
      );
    default:
      return null;
  }
};

// ── Main ChatBubble ───────────────────────────────────────────────────────────
const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  blocks,
  sender,
  messageId,
  onViewLog
}) => {
  const isUser = sender === "user";

  if (isUser) {
    return (
      <div className={`${styles.chatBubble} ${styles.user}`}>
        {message}
      </div>
    );
  }

  const hasBlocks = blocks && blocks.length > 0;

  return (
    <div className={styles.assistant}>
      <div className={styles.assistantBody}>

        {hasBlocks ? (
          // Render block schema — show summary text above cards if present
          <>
            {message && (
              <p style={{ fontSize: 14, color: "#374151", marginBottom: 12 }}>
                {message}
              </p>
            )}
            {blocks.map((block, idx) => (
              <RenderBlock key={idx} block={block} />
            ))}
          </>
        ) : (
          // Markdown render — handles **bold**, paragraphs, tables, etc.
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => (
                <p style={{ fontSize: 14, color: "#374151", marginBottom: 16, lineHeight: 1.6 }}>
                  {children}
                </p>
              ),
              strong: ({ children }) => (
                <strong style={{ fontWeight: 600, color: "#111827" }}>{children}</strong>
              ),
              hr: () => (
                <hr style={{ border: "none", borderTop: "1px solid #f3f4f6", margin: "12px 0" }} />
              ),
              table: ({ children }) => (
                <div style={{ marginBottom: 16, marginTop: 8, width: "100%" }}>
                  <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                    border: "1px solid #e5e7eb",
                    tableLayout: "fixed",
                  }}>
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }) => (
                <thead style={{ background: "#f9fafb" }}>
                  {children}
                </thead>
              ),
              th: ({ children }) => (
                <th style={{
                  textAlign: "left",
                  padding: "10px 14px",
                  borderBottom: "2px solid #e5e7eb",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  letterSpacing: 0.3,
                }}>
                  {children}
                </th>
              ),
              tbody: ({ children }) => (
                <tbody>{children}</tbody>
              ),
              tr: ({ children }) => (
                <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
                  {children}
                </tr>
              ),
              td: ({ children }) => (
                <td style={{
                  padding: "10px 14px",
                  color: "#374151",
                  fontSize: 13,
                  verticalAlign: "top",
                  lineHeight: 1.5,
                  wordBreak: "break-word",
                }}>
                  {children}
                </td>
              ),
            }}
          >
            {message}
          </ReactMarkdown>
        )}

        {sender === "assistant" && messageId && (
          <div style={{ marginTop: 10 }}>
            <button
              onClick={() => onViewLog?.(messageId)}
              style={{
                fontSize: 12,
                opacity: 0.6,
                cursor: "pointer",
                background: "transparent",
                border: "none",
                color: "#888",
                padding: 0
              }}
            >
              View Log
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default ChatBubble;
